import React, { useState, useEffect } from 'react';
import { FiDownload as Download, FiFile as File, FiClock as Clock } from 'react-icons/fi';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useOfflinePolicies } from '../hooks/useOfflinePolicies';
import { AGENCY_PHONE_DISPLAY, AGENCY_PHONE_TEL } from '../constants/contact';
import DigitalIdCard from './DigitalIdCard';
import supabase from '../supabase/supabase';

const BUCKET = 'documents';
const ID_CARD_TYPES = ['id card', 'id_card', 'insurance id'];

const IDCardsView = () => {
  const { user } = useAuth();
  const isOnline = useOnlineStatus();
  const { policies, isFromCache, cachedAt } = useOfflinePolicies(user);

  const [idCardDocs, setIdCardDocs] = useState([]);
  const [docsError, setDocsError] = useState('');

  const holderName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  // Carrier-issued PDFs are a bonus on top of the offline cards, so a failure
  // here never blocks the page from rendering.
  useEffect(() => {
    if (!isOnline) return;
    const clientId = user?.clientId;
    if (!clientId) {
      setIdCardDocs([]);
      return;
    }

    let cancelled = false;
    const loadIdCards = async () => {
      setDocsError('');
      try {
        const { data, error } = await supabase
          .from('documents')
          .select('*')
          .eq('client_id', clientId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        if (cancelled) return;

        setIdCardDocs(
          (data || []).filter((doc) =>
            ID_CARD_TYPES.some((t) => (doc.document_type || '').toLowerCase().includes(t))
          )
        );
      } catch {
        if (!cancelled) setDocsError('Your uploaded ID card files could not be loaded right now.');
      }
    };

    loadIdCards();
    return () => {
      cancelled = true;
    };
  }, [user?.clientId, isOnline]);

  const openDoc = async (doc, download) => {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(doc.file_path, 3600);
    const url = data?.signedUrl;
    if (error || !url) {
      setDocsError(`That file is unavailable — please contact us at ${AGENCY_PHONE_DISPLAY}.`);
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    if (download) a.download = doc.file_name || 'id-card';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Insurance ID Cards</h1>
        <p className="text-gray-600">
          Your coverage details, saved to this device so they're available even without signal.
        </p>
        {isFromCache && cachedAt && (
          <p className="mt-3 inline-flex items-center gap-2 text-sm text-gray-600 bg-gray-100 rounded-lg px-3 py-2">
            <Clock size={15} />
            Saved copy from {format(new Date(cachedAt), 'MMM d, yyyy h:mm a')}
          </p>
        )}
      </div>

      {policies.length > 0 ? (
        <div className="grid gap-5 lg:grid-cols-2">
          {policies.map((policy) => (
            <DigitalIdCard key={policy.id} policy={policy} holderName={holderName} />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 sm:p-12 text-center">
          <File size={44} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No current policy ID cards on file
          </h3>
          {/* Never phrased as "you have no coverage". Renewals are entered as
              new rows and the CRM lags, so an empty surface here is at least as
              likely to mean our records are behind as it is to mean the client
              lapsed. The copy has to carry that, because this is the screen a
              client reaches when they need proof and cannot find it. */}
          <p className="text-gray-600">
            If you have active coverage with us and don&apos;t see it here, call or
            text us at{' '}
            <a href={AGENCY_PHONE_TEL} className="text-[#0038A8] font-semibold hover:underline">
              {AGENCY_PHONE_DISPLAY}
            </a>{' '}
            — our records may need updating.
          </p>
        </div>
      )}

      {/* Carrier-issued documents, when the agency has uploaded them. */}
      {idCardDocs.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-1">Carrier-issued ID cards</h2>
          <p className="text-sm text-gray-600 mb-4">
            Official documents from your carrier. These need a connection to open.
          </p>
          <ul className="divide-y divide-gray-100">
            {idCardDocs.map((doc) => (
              <li key={doc.id} className="py-3 flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 truncate">{doc.file_name}</div>
                  <div className="text-xs text-gray-500">
                    {doc.document_type} · {format(new Date(doc.created_at), 'MM/dd/yyyy')}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => openDoc(doc, false)}
                    className="px-3 py-2 rounded-lg text-sm font-semibold text-[#DC143C] hover:bg-red-50 transition-colors"
                  >
                    View
                  </button>
                  <button
                    onClick={() => openDoc(doc, true)}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold border border-[#0038A8] text-[#0038A8] hover:bg-[#0038A8] hover:text-white transition-colors"
                  >
                    <Download size={15} /> Save
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {docsError && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
          {docsError}
        </div>
      )}
    </div>
  );
};

export default IDCardsView;
